import type { VfxTargetKind } from "./definition.ts";
import { NO_TINT } from "./tone.ts";

/**
 * The vfx feed (ADR-134 decision 4): the vfx the player has been asked for since the last frame, which the surface
 * (`world-ui/vfx/module.ts`) drains each frame, so neither names the other. A ring over typed arrays, so asking for a
 * vfx allocates nothing; when more are asked for than it holds, the newest are kept.
 */

/**
 * How many asked-for vfx the feed holds between two frames.
 */
export const VFX_FEED_SIZE = 32;

/**
 * One asked-for vfx: the character, the vfx's index in the registry, whether the character is the page's own, the tint
 * (`NO_TINT` for none) and the scale.
 */
export type VfxRequestVisitor = (handle: number, key: number, own: boolean, tint: number, scale: number) => void;

/**
 * The feed.
 */
export class VfxFeed {
  private readonly handles = new Int32Array(VFX_FEED_SIZE);
  private readonly keys = new Int32Array(VFX_FEED_SIZE);
  private readonly owns = new Uint8Array(VFX_FEED_SIZE);
  private readonly tints = new Int32Array(VFX_FEED_SIZE);
  private readonly scales = new Float32Array(VFX_FEED_SIZE);
  private first = 0;
  private size = 0;
  private readonly listeners = new Set<() => void>();

  /**
   * Asks for a vfx.
   *
   * @param handle The character's handle.
   * @param key The vfx's index in the registry.
   * @param own Whether it is the page's own character.
   * @param tint The tint as 0xRRGGBB, or `NO_TINT`.
   * @param scale The scale, 1 for none.
   */
  public push(handle: number, key: number, own: boolean, tint: number = NO_TINT, scale = 1): void {
    if (this.size === VFX_FEED_SIZE) {
      this.first = (this.first + 1) % VFX_FEED_SIZE;
      this.size -= 1;
    }
    const slot = (this.first + this.size) % VFX_FEED_SIZE;
    this.handles[slot] = handle;
    this.keys[slot] = key;
    this.owns[slot] = own ? 1 : 0;
    this.tints[slot] = tint;
    this.scales[slot] = scale;
    this.size += 1;
    for (const listener of this.listeners) {
      listener();
    }
  }

  /**
   * Calls a function after each vfx asked for, so a surface that sleeps while nothing runs can wake.
   *
   * @param listener The function.
   * @returns A function that stops the calls.
   */
  public listen(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Hands over every vfx held, oldest first, and empties the feed. One asked for while it hands over waits for the next call.
   *
   * @param visit Told each.
   */
  public take(visit: VfxRequestVisitor): void {
    const count = this.size;
    const first = this.first;
    this.first = (this.first + count) % VFX_FEED_SIZE;
    this.size = 0;
    for (let index = 0; index < count; index += 1) {
      const slot = (first + index) % VFX_FEED_SIZE;
      visit(this.handles[slot] as number, this.keys[slot] as number, this.owns[slot] === 1, this.tints[slot] as number, this.scales[slot] as number);
    }
  }

  /**
   * Forgets every vfx held, as a session's end must.
   */
  public clear(): void {
    this.first = 0;
    this.size = 0;
  }
}

/**
 * One feed for each target a vfx is drawn on.
 */
export type VfxFeeds = Readonly<Record<VfxTargetKind, VfxFeed>>;

/**
 * The page's feed of vfx on characters, as `PAGE_HITS` is its one hit feed.
 */
export const PAGE_VFX_FEED = new VfxFeed();

/**
 * The page's feed of vfx on the HUD's XP bar.
 */
export const PAGE_XP_BAR_FEED = new VfxFeed();

/**
 * The page's feeds.
 */
export const PAGE_VFX_FEEDS: VfxFeeds = Object.freeze({ character: PAGE_VFX_FEED, xpBar: PAGE_XP_BAR_FEED });
